import { createElement, useCallback } from 'react';
import { SearchLocationIdentifier } from '@sas/nova-core/services/drive';
import { CatalogSearch } from '../../data/CatalogSearch';
import { useContentNavigationDispatch, useContentSelectionDispatch } from '../providers';
import { ContentSearchFieldProps } from './types';
import bundle from './NovaContent-ContentSearchField-gui-icu.properties';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';

const useTextLocalizer = createUseTextLocalizer(bundle);

export const ContentSearchField = (props: ContentSearchFieldProps) => {
  const localize = useTextLocalizer();
  const navDispatch = useContentNavigationDispatch();
  const selectionDispatch = useContentSelectionDispatch();

  const onSearch = useCallback<NonNullable<ContentSearchFieldProps['onSearch']>>(
    (query, indices) => {
      // For the content components "doing a search" really just means setting the current location
      // to a search location, which the components will then respond to accordingly via the nav provider
      const location: SearchLocationIdentifier = {
        type: 'searchLocation',
        value: {
          name: query,
          searchQuery: query,
          idQualifier: getUniqueIdQualifier(query),
        },
      };

      navDispatch?.({ location, locationContextPath: [location] });
      selectionDispatch?.([]);

      props.onSearch?.(query, indices);
    },
    [navDispatch, props, selectionDispatch]
  );

  return (
    <CatalogSearch
      initialQuery={''}
      enableExperimental={false}
      onCurrentSearchIndicesChanged={() => {}}
      {...props}
      placeholder={props.placeholder ?? localize('content.placeholder.label')}
      onSearch={onSearch}
    />
  );
};

const uniqueIdQualifierMap: Map<string, number> = new Map();

// Generate a unique idQualifier by adding an incremented value tracked in a map. Will grow over
// time, but should not be unreasonable due to limited nature of searches per session
const getUniqueIdQualifier = (query: string): string => {
  const nextUniqueNum = uniqueIdQualifierMap.get(query);

  if (nextUniqueNum) {
    uniqueIdQualifierMap.set(query, nextUniqueNum + 1);
    return `${query} ${nextUniqueNum}`;
  } else {
    uniqueIdQualifierMap.set(query, 1);
    return `${query}`;
  }
};
